/**
 * Activity-Notification Bridge Service
 *
 * Links activity tracking with automatic notification creation.
 * Implements 2026 best practice: smart service layer pattern.
 *
 * Key Features:
 * - Automatic notification creation from activity logs
 * - Activity-to-notification type mapping
 * - Notification deduplication
 * - Preference checking integration
 * - Batch notification support
 *
 * @module activity-notification-bridge
 * @since 2026-02-18
 */
import type { IService, ServiceResponse } from "./service-registry";
import { notificationManager } from "./notifications/notification-manager.service";
import { notificationPreferencesService } from "./notifications/notification-preferences.service";
import type { NotificationType } from "../types/notification";
import type { NotificationCategory } from "../types/notificationPreferences";
import { logger } from "../lib/logger";
import { type EventDomain } from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
// ============================================================================
// TYPES & INTERFACES
// ============================================================================
export interface ActivityParams {
    sourceType: string;
    sourceId: string;
    actionType: string;
    userId: string;
    actorId?: string;
    targetUserId?: string;
    details?: Record<string, unknown>;
}
export interface NotificationMapping {
    notificationType: NotificationType;
    shouldNotify: boolean;
    requiresPreferenceCheck?: boolean;
    preferenceCategory?: NotificationCategory;
}
// ============================================================================
// ACTIVITY-TO-NOTIFICATION MAPPING
// ============================================================================
/**
 * Maps activity action types to notification types
 * This is the central configuration for which activities create notifications
 */
const ACTIVITY_NOTIFICATION_MAP: Record<string, NotificationMapping> = {
    // Task Activities
    "task_created": {
        notificationType: "TASK_ASSIGNED" as NotificationType,
        shouldNotify: false, // Don't notify on creation, only on assignment
    },
    "task_assigned": {
        notificationType: "TASK_ASSIGNED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "task_assignment",
    },
    "task_completed": {
        notificationType: "TASK_COMPLETED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "subtask_completion",
    },
    "task_status_changed": {
        notificationType: "TASK_STATUS_CHANGED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "task_status_change",
    },
    "task_priority_changed": {
        notificationType: "TASK_PRIORITY_CHANGED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "task_priority_change",
    },
    "task_comment_added": {
        notificationType: "TASK_COMMENT" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "task_comment",
    },
    "task_mention": {
        notificationType: "TASK_MENTION" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "task_mention",
    },
    "task_due_soon": {
        notificationType: "TASK_DUE_REMINDER" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "task_due_reminder",
    },
    "task_overdue": {
        notificationType: "TASK_OVERDUE" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "task_overdue_alert",
    },
    "subtask_completed": {
        notificationType: "SUBTASK_COMPLETED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "subtask_completion",
    },
    // Project Activities (NEW)
    "project_created": {
        notificationType: "SYSTEM_ANNOUNCEMENT" as NotificationType, // Use system announcement for now
        shouldNotify: true,
        requiresPreferenceCheck: false,
    },
    "project_assigned": {
        notificationType: "TASK_ASSIGNED" as NotificationType, // Reuse task assigned type
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "task_assignment",
    },
    "project_status_changed": {
        notificationType: "TASK_STATUS_CHANGED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "task_status_change",
    },
    // Gamification Activities (NEW)
    "xp_awarded": {
        notificationType: "XP_MILESTONE" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "xp_milestone",
    },
    "level_up": {
        notificationType: "XP_MILESTONE" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "xp_milestone",
    },
    "streak_milestone": {
        notificationType: "STREAK_BONUS" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "streak_bonus",
    },
    "leaderboard_position_changed": {
        notificationType: "LEADERBOARD_UPDATE" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "leaderboard_update",
    },
    // Achievement Activities (NEW)
    "achievement_earned": {
        notificationType: "ACHIEVEMENT_BADGE" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "achievement_badge",
    },
    "badge_unlocked": {
        notificationType: "ACHIEVEMENT_BADGE" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "achievement_badge",
    },
    // Community Activities
    "post_liked": {
        notificationType: "POST_LIKED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "post_like",
    },
    "post_commented": {
        notificationType: "POST_COMMENTED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "post_comment",
    },
    "comment_replied": {
        notificationType: "COMMENT_REPLIED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "comment_reply",
    },
    "community_mention": {
        notificationType: "COMMUNITY_MENTION" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "community_mention",
    },
    // Admin Activities
    "role_changed": {
        notificationType: "ROLE_CHANGED" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "role_change",
    },
    "system_announcement": {
        notificationType: "SYSTEM_ANNOUNCEMENT" as NotificationType,
        shouldNotify: true,
        requiresPreferenceCheck: true,
        preferenceCategory: "system_announcement",
    },
};
// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================
export interface IActivityNotificationBridge extends IService {
    processActivity(params: ActivityParams): Promise<ServiceResponse<void>>;
    shouldCreateNotification(actionType: string, userId: string): Promise<boolean>;
    getNotificationMapping(actionType: string): NotificationMapping | null;
}
export class ActivityNotificationBridge implements IActivityNotificationBridge {
    readonly name = "ActivityNotificationBridge";
    private recentNotifications = new Map<string, number>(); // For deduplication
    private readonly DEDUP_WINDOW_MS = 5000; // 5 seconds
    async initialize(): Promise<void> {
        logger.info("✅ ActivityNotificationBridge initialized");
        // Clear dedup cache periodically
        setInterval(() => {
            const now = Date.now();
            for (const [key, timestamp] of this.recentNotifications.entries()) {
                if (now - timestamp > this.DEDUP_WINDOW_MS) {
                    this.recentNotifications.delete(key);
                }
            }
        }, 10000); // Cleanup every 10 seconds
    }
    async dispose(): Promise<void> {
        logger.info("🧹 ActivityNotificationBridge disposed");
        this.recentNotifications.clear();
    }
    /**
     * Get notification mapping for an activity action type
     */
    getNotificationMapping(actionType: string): NotificationMapping | null {
        return ACTIVITY_NOTIFICATION_MAP[actionType] || null;
    }
    /**
     * Check if a notification should be created for this activity
     */
    async shouldCreateNotification(actionType: string, userId: string): Promise<boolean> {
        const mapping = this.getNotificationMapping(actionType);
        if (!mapping || !mapping.shouldNotify) {
            return false;
        }
        // Check user preferences if required
        if (mapping.requiresPreferenceCheck && mapping.preferenceCategory) {
            const prefsResponse = await notificationPreferencesService.shouldSendNotification(userId, mapping.preferenceCategory);
            if (!prefsResponse.success || !prefsResponse.data) {
                logger.debug("User has disabled notifications for category", {
                    userId,
                    category: mapping.preferenceCategory,
                });
                return false;
            }
        }
        return true;
    }
    /**
     * Process an activity and create notification if appropriate
     *
     * This is the main entry point that services should call
     */
    async processActivity(params: ActivityParams): Promise<ServiceResponse<void>> {
        try {
            const { sourceType, sourceId, actionType, userId, actorId, targetUserId, details, } = params;
            // MYCELIUM PROTOCOL: Publish activity event to event backbone
            // This enables other services (gamification, analytics, AI) to react
            const domain = this.mapSourceTypeToDomain(sourceType);
            await getMyceliumTransport().publish({
                domain,
                type: actionType,
                userId,
                payload: {
                    sourceType,
                    sourceId,
                    actionType,
                    actorId,
                    targetUserId,
                    details: details || {},
                    source: "ActivityNotificationBridge",
                },
            }).catch(error => {
                // Don't fail the entire operation if event publishing fails
                logger.warn("Failed to publish activity event to mycelium", {
                    actionType,
                    error: error instanceof Error ? error.message : "Unknown error",
                });
            });
            // Determine who should receive the notification
            const recipientUserId = targetUserId || userId;
            // Check if notification should be created
            const shouldNotify = await this.shouldCreateNotification(actionType, recipientUserId);
            if (!shouldNotify) {
                logger.debug("Skipping notification creation", { actionType, recipientUserId });
                return { success: true, data: undefined };
            }
            // Check for duplicate notifications (deduplication)
            const dedupKey = `${recipientUserId}:${actionType}:${sourceId}`;
            const lastNotificationTime = this.recentNotifications.get(dedupKey);
            const now = Date.now();
            if (lastNotificationTime && (now - lastNotificationTime) < this.DEDUP_WINDOW_MS) {
                logger.debug("Skipping duplicate notification", {
                    dedupKey,
                    timeSinceLastMs: now - lastNotificationTime,
                });
                return { success: true, data: undefined };
            }
            // Get notification mapping
            const mapping = this.getNotificationMapping(actionType);
            if (!mapping) {
                logger.warn("No notification mapping found for action type", { actionType });
                return { success: true, data: undefined };
            }
            // Create notification
            const notificationData = {
                user_id: recipientUserId,
                actor_id: actorId || userId,
                type: mapping.notificationType,
                entity_type: sourceType,
                entity_id: sourceId,
                message_preview: this.generateMessagePreview(actionType, details),
                data: {
                    ...details,
                    activity_action: actionType,
                    created_via: "activity_bridge",
                },
            };
            const response = await notificationManager.createNotification(notificationData);
            if (!response.error) {
                // Track for deduplication
                this.recentNotifications.set(dedupKey, now);
                logger.info("Notification created from activity", {
                    actionType,
                    recipientUserId,
                    notificationId: response.data?.id,
                });
            }
            else {
                logger.error("Failed to create notification from activity", {
                    actionType,
                    error: response.error,
                });
            }
            return { success: true, data: undefined };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            logger.error("Error processing activity for notification", {
                error: serviceError,
                params,
            });
            // Don't fail the activity log if notification creation fails
            return { success: true, data: undefined };
        }
    }
    /**
     * Map activity source type to mycelium event domain
     */
    private mapSourceTypeToDomain(sourceType: string): EventDomain {
        const domainMap: Record<string, EventDomain> = {
            task: "task",
            sales: "sales",
            opportunity: "sales",
            calendar: "calendar",
            event: "calendar",
            xero: "accounting",
            invoice: "accounting",
            expense: "accounting",
            post: "team",
            comment: "team",
            brain_dump: "ai",
            user: "user",
            admin: "system",
        };
        return domainMap[sourceType] || "system";
    }
    /**
     * Generate user-friendly message preview from activity details
     */
    private generateMessagePreview(actionType: string, details?: Record<string, unknown>): string {
        const defaultMessages: Record<string, string> = {
            task_assigned: "You've been assigned a new task",
            task_completed: "A task has been completed",
            task_status_changed: "Task status has changed",
            task_priority_changed: "Task priority has changed",
            task_comment_added: "New comment on task",
            task_mention: "You were mentioned in a task",
            task_due_soon: "Task due soon",
            task_overdue: "Task is overdue",
            subtask_completed: "Subtask completed",
            project_created: "New project created",
            project_assigned: "You've been assigned to a project",
            project_status_changed: "Project status changed",
            xp_awarded: "You earned XP!",
            level_up: "Level up! 🎉",
            streak_milestone: "Streak milestone achieved! 🔥",
            leaderboard_position_changed: "Your leaderboard position changed",
            achievement_earned: "Achievement unlocked! 🏆",
            badge_unlocked: "New badge earned!",
            post_liked: "Someone liked your post",
            post_commented: "New comment on your post",
            comment_replied: "Someone replied to your comment",
            community_mention: "You were mentioned in the community",
            role_changed: "Your role has been changed",
            system_announcement: "System announcement",
        };
        let message = defaultMessages[actionType] || "New activity";
        // Enhance with details if available
        if (details) {
            if (details.title) {
                message += `: ${details.title}`;
            }
            else if (details.name) {
                message += `: ${details.name}`;
            }
            else if (details.taskTitle) {
                message += `: ${details.taskTitle}`;
            }
            else if (details.projectName) {
                message += `: ${details.projectName}`;
            }
        }
        return message;
    }
}
// ============================================================================
// SINGLETON EXPORT
// ============================================================================
export const activityNotificationBridge = new ActivityNotificationBridge();
